import DataGrid, {Column, Editing, Toolbar, Item, DataGridRef} from "devextreme-react/data-grid";
import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import useApi, {IApiErreur} from "../../hooks/useApi";
import Guid from "../../models/Guid";
import IReservationDetailOccupant from "../../models/IReservationDetailOccupant";
import CustomStore from "devextreme/data/custom_store";
import {useAuth} from "../../contexts/auth";
import dxDataGrid, {EditorPreparingEvent, FocusedRowChangedEvent, InitNewRowEvent} from "devextreme/ui/data_grid";
import {Button} from "devextreme-react";
import useClient from "../../components/selectbox/client";
import {useContextPopupPage} from "../../components/popups/PopupPage";
import IReservationDetailSommaire from "../../models/IReservationDetailSommaire";
import DataSource from "devextreme/data/data_source";
import IDataSource from "../../models/IDataSource";
import IReservationList from "../../models/IReservationList";
import IGestionReservation from "../../models/IGestionReservation";
import {differenceInYears} from "date-fns";
import {be} from "date-fns/locale";
import IClient from "../../models/IClient";
import {ICustomRule, Mode} from "../../hooks/useCustomDataSource";

interface IPopupOccupantProps {
    reservationDetailSommaireParent?: IReservationDetailSommaire,
    reservationFormDataSource: IDataSource<IReservationList, IGestionReservation>,
    saveBtnRef?: any
}

const PopUpOccupant = ({reservationFormDataSource, reservationDetailSommaireParent, saveBtnRef}: IPopupOccupantProps) => {
    const [occupants, setOccupants] = useState<IReservationDetailOccupant[]>(reservationDetailSommaireParent?.reservationDetailOccupants || []);
    const {user} = useAuth();
    const {isPopupGestionClientOpen, setIsPopupGestionClientOpen, PopupGestionClient} = useClient();
    const {data: occupant, getData: getOccupant, setData: setOccupant, postData: postOccupant, fetchData} = useApi<IReservationDetailOccupant>(`ReservationDetailOccupant`);
    const [focusedRow, setFocusedRow] = useState<IReservationDetailOccupant>();
    const dataGridRef = useRef<DataGridRef>(null);
    const {
        selectedDataRow: selectedReservation,
        mode,
        isFieldModifier,
    } = reservationFormDataSource;
    const [validationRules, setValidationRules] = useState<ICustomRule[]>([]);

    useEffect(() => {
        fetchData("get", {customPath: `reservationDetailOccupant/getValidation`, returnOnly: true}).then((result: string[]) => {
            if (result) {
                setValidationRules(result.map(fieldName => {
                    return {type: "async", message: "", reevaluate: true, propertyName: fieldName} as ICustomRule
                }))
                dataGridRef.current?.instance().refresh()
            }
        })

    }, [dataGridRef])

    const validate = useCallback(async (data: IReservationDetailSommaire | undefined) => {
        const result = await fetchData("post", {
            customPath: `reservationDetailOccupant/ValidateModel`,
            body: data as unknown as object,
            returnOnly: true
        });
        return result as unknown[] as IApiErreur[];
    }, [fetchData]);

    const validationCallback = useCallback((e: any) => {
        return new Promise(async (resolve, reject) => {
            if (mode === Mode.Lecture || isPopupGestionClientOpen) return resolve(undefined);
            try {

                const result: IApiErreur[] = await validate(e.data);
                const erreur = result.find(f => f.propertyName.toLowerCase() === e.rule.propertyName.toLowerCase())
                if (erreur) {
                    reject(erreur.errorMessage);
                }
            } catch (ex) {
                resolve(undefined);
            }
            resolve(undefined);
        })
    }, [mode, validate, isPopupGestionClientOpen])

    const customizeColumns = useCallback((items: any) => {
        if (mode === Mode.Lecture || !validationRules || validationRules.length === 0) return;

        items.forEach((col: any) => {
            if (col.dataField) {
                const rule = validationRules.find(f => f.propertyName.toLowerCase() === col.dataField?.toLowerCase());
                if (rule) {
                    rule.validationCallback = validationCallback;
                    col.validationRules = [rule];
                }
            }
        });

    }, [validationRules, mode, validationCallback])

    const dataGridFilterValue = useMemo(() => ["dateSuppression", "=", null], []);
    const dataStoreOccupants = useMemo(() => new CustomStore({
        key: "idReservationDetailOccupant",
        load: () => occupants,
        insert: async (values) => {
            const newOccupants = [...occupants, values];
            setOccupants(newOccupants);
            return values;
        },
        update: async (key, values) => {
            const updatedData = occupants.map((occupant) => {
                if (occupant.idReservationDetailOccupant?.equals(key)) {
                    return {...occupant, ...values};
                }
                return occupant;
            });
            setOccupants(updatedData);
        },
        remove: async key => {
            const updatedData = occupants.map((occupant) => {
                if (occupant.idReservationDetailOccupant?.equals(key)) {
                    return {...occupant, dateSuppression: new Date()};
                }
                return occupant;
            });
            setOccupants(updatedData);
        }
    }), [occupants]);

    useImperativeHandle(saveBtnRef, () => ({
        getData() {
            return occupants;
        }
    }))

    const dataSourceOccupants = useMemo(() => new DataSource({key: 'idReservationDetailOccupant', store: dataStoreOccupants, reshapeOnPush: true}), [dataStoreOccupants])

    const onInitNewRow = useCallback(async (e: InitNewRowEvent<IReservationDetailOccupant>) => {
        e.promise = getOccupant({customPath: 'reservationDetailOccupant/default', returnOnly: true})
            .then((newRow) => {
                e.data = {...newRow, idUsagerCreation: user?.idUsager || newRow.idUsagerCreation, idUsagerModification: user?.idUsager || newRow.idUsagerModification};
            });
    }, [getOccupant, user?.idUsager]);


    const onEditorPreparing = useCallback((e: EditorPreparingEvent) => {
        if (e.parentType === "dataRow") {
            if (e.dataField == "nom" || e.dataField == "prenom") {
                e.editorOptions.disabled = e.row?.data && !e.row.data.idClient.equals(Guid.empty);
            }
        }
    }, [])

    const onFocusedRowChanged = useCallback((e: FocusedRowChangedEvent) => {
        setFocusedRow(e.row?.data);
    }, []);

    const calculateDisplayValueAge = useCallback((e: IReservationDetailOccupant) => {
        if (!e.dateNaissance) return null
        return differenceInYears(new Date(), e.dateNaissance)
    }, []);

    const dateNaissanceEditorOptions = useMemo(() => ({dateType: 'date', useMaskBehavior: true}), []);
    const onSelectionchanged = useCallback(async (selectedClient: IClient) => {
        dataSourceOccupants.store().update(focusedRow?.idReservationDetailOccupant, {nom: selectedClient.nom, prenom: selectedClient.prenom, idClient: selectedClient.idClient, dateNaissance: selectedClient.dateNaissance});
    }, [focusedRow?.idReservationDetailOccupant, dataSourceOccupants]);

    const findClientCellRender = useCallback((e: any) => {
        const onClickFindClient = async () => {
            await dataGridRef.current?.instance().saveEditData();
            setFocusedRow(e.data)
            setIsPopupGestionClientOpen(true)
        };
        return <Button icon={"find"} stylingMode={"text"} onClick={onClickFindClient}/>;
    }, [setIsPopupGestionClientOpen]);

    return (
        <div style={{height: "100%"}}>
            <DataGrid
                ref={dataGridRef}
                showBorders={true}
                dataSource={dataSourceOccupants}
                height={"100%"}
                focusedRowEnabled={true}
                autoNavigateToFocusedRow={false}
                onEditorPreparing={onEditorPreparing}
                onInitNewRow={onInitNewRow}
                filterValue={dataGridFilterValue}
                onFocusedRowChanged={onFocusedRowChanged}
                customizeColumns={customizeColumns}
            >
                <Toolbar>
                    <Item name={"addRowButton"} showText={"always"} location={"before"}/>
                </Toolbar>
                <Editing newRowPosition={"first"} mode={"cell"} startEditAction={"click"} allowAdding={mode !== Mode.Lecture} allowDeleting={true} allowUpdating={mode !== Mode.Lecture}/>
                <Column dataType={"string"} dataField={"nom"}/>
                <Column dataType={"string"} dataField={"prenom"} caption={"Prénom"}/>
                <Column width={"30%"} dataType={"string"} dataField={"note"}/>
                <Column width={"10%"} dataType={"date"} dataField={"dateNaissance"} format={"dd-MM-yyyy"} editorOptions={dateNaissanceEditorOptions}/>
                <Column dataType={"number"} caption={"Âge"} calculateDisplayValue={calculateDisplayValueAge}/>
                <Column dataType={"boolean"} dataField={"cosignataire"}/>
                <Column dataType={"date"} dataField={"dateSuppression"} visible={false}/>
                <Column dataType={"object"} visible={mode !== Mode.Lecture} cellRender={findClientCellRender} width={"36px"}/>
                <Column type="buttons" width={"50px"}/>
            </DataGrid>
            <PopupGestionClient isModeSelection={mode !== Mode.Lecture} onSelectionchanged={onSelectionchanged}/>
        </div>
    )
}

export default PopUpOccupant;